import React, { useMemo } from 'react';

import {
  Alert,
  Divider,
  Grid, Skeleton, Stack, Typography,
} from '@mui/material';
import { toInteger } from 'lodash';

import Button from '../../../../../components/Button';
import Chip from '../../../../../components/Chip';
import ContentCard from '../../../../../components/ContentCard';
import EditField from '../../../../../components/EditField';
import { useGetStudentLessonEnrolmentsQuery } from '../../../../../services/classes';
import { useGetCourseQuery } from '../../../../../services/courses';
import { useGetStreamQuery } from '../../../../../services/streams';
import {
  useNewInvoiceMutation,
  useUpdateCreditsMutation,
} from '../../../../../services/students';
import { CourseState, Student } from '../../../../../services/students/types';
import { CourseEnrolmentState } from '../../../../../types/common';
import { getLessonEnrolmentCourse } from '../../../../../utils/common/classes';
import useError from '../../../../../utils/react/hooks/useError';
import useSuccess from '../../../../../utils/react/hooks/useSuccess';
import { getBillingChip } from '../utils';

interface BillingRowProps {
  student: Student,
  courseState: CourseState
}

const BillingRow = ({ student, courseState }: BillingRowProps) => {
  const { data: course, isLoading: isCourseLoading } = useGetCourseQuery({
    courseId: courseState.course,
  });

  const streams = useMemo(
    () => student.classes
      .filter((x) => x.course === courseState.course)
      .map((x) => x.stream),
    [student, courseState],
  );
  const { data: stream, isLoading: isStreamLoading } = useGetStreamQuery({
    course: courseState.course,
    streamId: streams[0] || '',
  }, { skip: streams.length !== 1 });
  const [updateCredits] = useUpdateCreditsMutation();

  const { data: lessonEnrolments } = useGetStudentLessonEnrolmentsQuery({
    studentId: student?.id ?? '',
  }, { skip: !student });

  const unmarkedLessonEnrolmentsCount = useMemo(() => {
    const x = lessonEnrolments?.data
      .filter((lessonEnrolment) => getLessonEnrolmentCourse(lessonEnrolment) === courseState.course
     && !lessonEnrolment.rollMarked);

    return x?.length || '?';
  }, [lessonEnrolments, courseState]);

  const updateTermCredits = async (newCredits: number) => updateCredits({
    studentId: student.studentId,
    subject: courseState.subject,
    course: courseState.course,
    credits: {
      termCredits: newCredits,
    },
  });

  const updateExtraCredits = async (newCredits: number) => updateCredits({
    studentId: student.studentId,
    subject: courseState.subject,
    course: courseState.course,
    credits: {
      extraCredits: newCredits,
    },
  });

  if (isCourseLoading || isStreamLoading) {
    return (
      <Skeleton variant="rectangular" height={64} />
    );
  }

  return (
    <Stack direction="column" spacing={1}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Typography variant="subtitle2">
          {course?.data.label}
        </Typography>
        <Chip
          size="small"
          color="gray"
          variant="outlined"
          label={streams.length > 1
            ? 'Multiple Streams'
            : `${stream?.data.creditsPerTerm || course?.data.creditsPerTerm} x ${stream?.data.terms || course?.data.terms}`}
        />
        <Chip
          size="small"
          {...getBillingChip(courseState.state)}
        />
        <Chip
          size="small"
          color="gray"
          label={`${courseState.lessonsCompleted} Done`}
        />
        <Chip
          size="small"
          color="gray"
          label={`${unmarkedLessonEnrolmentsCount} Left`}
        />
        {courseState.consecutiveAbsences > 0 && (
          <Chip
            size="small"
            color="red"
            variant="outlined"
            label={`Absence Streak: ${courseState.consecutiveAbsences}`}
          />
        )}
      </Stack>
      <Grid container direction="row" spacing={1} alignItems="center">
        <Grid item xs={6}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">Term Credits</Typography>
            <EditField
              value={courseState.termCredits}
              type="number"
              onSubmit={(newCount) => updateTermCredits(toInteger(newCount))}
            />
          </Stack>
        </Grid>
        <Grid item xs={6}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2">Extra Credits</Typography>
            <EditField
              value={courseState.extraCredits}
              type="number"
              onSubmit={(newCount) => updateExtraCredits(toInteger(newCount))}
            />
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

interface BillingCreditsCardProps {
  courseStates: {
    [key: string]: CourseState,
  },
  student?: Student,
}

const BillingCreditsCard = ({ courseStates, student }: BillingCreditsCardProps) => {
  const [createInvoice, {
    isLoading: isInvoiceCreateLoading,
    isSuccess: isInvoiceCreateSuccess,
    isError: isInvoiceCreateError,
    error: invoiceCreateError,
  }] = useNewInvoiceMutation();

  useSuccess(isInvoiceCreateSuccess, 'Invoice created');
  useError(isInvoiceCreateError, invoiceCreateError);

  if (!student || !courseStates) {
    return (
      <Skeleton variant="rectangular" height={64} />
    );
  }

  return (
    <ContentCard variant="outlined" title="Billing Credits">
      {Object.keys(courseStates).length > 0 ? (
        <Stack direction="column" spacing={1}>
          <Alert severity="warning">
            Absence streaks are tallied by the <b>most recent</b> lesson roll mark only.
          </Alert>
          {Object.values(courseStates || {})
            .filter((courseState) => !(courseState.state === CourseEnrolmentState.Unenrolled
                       && courseState.extraCredits === 0
                       && courseState.termCredits === 0))
            .map((courseState) => (
              <BillingRow
                key={courseState.course}
                student={student}
                courseState={courseState}
              />
            ))}
          <Divider />
          <Stack direction="row" spacing={1} justifyContent="right">
            <Button
              disabled={!student?.requiresInvoice || !student?.xeroId}
              loading={isInvoiceCreateLoading}
              onClick={async () => createInvoice({ studentId: student.id })}
            >
              Generate Invoice
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Typography variant="overline">
          Not available
        </Typography>
      )}
    </ContentCard>
  );
};

export default BillingCreditsCard;
